// The learner's microphone: speech-to-text via the Web Speech API
// (`SpeechRecognition` / `webkitSpeechRecognition`).
//
// This is the browser counterpart of the recogniser half of the Android build's
// `speech/SpeechController.kt`, and keeps its most important behaviour: a
// hands-free session stays latched open across pauses. Each finished utterance
// is delivered to `onResult` and the recogniser is restarted immediately, so
// thinking mid-sentence never drops the learner out of the conversation. Only
// `stopListening()` — the second mic tap — ends the session.
//
// Browsers do not expose the recogniser as uniformly as Android does:
//   * Chrome and Safari ship it; Firefox does not, so the app falls back to
//     typing and says so.
//   * `continuous` is honoured inconsistently, and every engine eventually
//     fires `end` on its own, so the restart-on-end loop below is what actually
//     keeps a session alive rather than the flag.
//   * Permission is requested by the recogniser itself on first use; there is
//     no separate runtime-permission step to mirror.

/** The slice of the SpeechRecognition API this module uses. */
interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
}

type RecognitionConstructor = new () => SpeechRecognitionLike;

function constructorFor(): RecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function isRecognitionSupported(): boolean {
  return constructorFor() !== null;
}

export interface ListenCallbacks {
  /** Fires repeatedly while the learner is still speaking. */
  onPartial: (text: string) => void;
  /** Fires once per finished utterance. */
  onResult: (text: string) => void;
  /** Fires with a human-readable message; the session is over by then. */
  onError: (message: string) => void;
}

interface Session extends ListenCallbacks {
  continuous: boolean;
  /** Set once this session has produced anything, for the single-shot case. */
  heardSomething: boolean;
}

const RESTART_DELAY_MS = 250;
const MAX_CONSECUTIVE_FAILURES = 3;
/**
 * A turn this short that produced nothing means the engine refused rather than
 * that the learner was quiet: a real silent turn still runs for seconds before
 * the engine gives up on it.
 */
const MIN_PRODUCTIVE_TURN_MS = 1200;
const MAX_UNPRODUCTIVE_TURNS = 5;

let session: Session | null = null;
let recognition: SpeechRecognitionLike | null = null;
let restartTimer: ReturnType<typeof setTimeout> | null = null;
/**
 * Consecutive engine failures that aren't just silence. A hands-free session
 * restarts through the odd hiccup but gives up if the engine keeps refusing.
 */
let consecutiveFailures = 0;
/** When the current turn's recogniser was started, for the guard below. */
let turnStartedAt = 0;
/** Consecutive turns that ended instantly without hearing anything. */
let unproductiveTurns = 0;

export function isListening(): boolean {
  return session !== null;
}

/**
 * True when the app is running inside an iframe. The microphone's permission
 * policy defaults to `self`, so an embed on another site (a blog post, say)
 * cannot use it unless that page opts in with `allow="microphone"` — worth
 * saying out loud, because the failure otherwise looks like a broken app.
 */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Reading window.top across origins throws, which itself means embedded.
    return true;
  }
}

/**
 * Starts listening. With `continuous` the microphone stays latched open until
 * `stopListening()`; without it the session ends after one utterance, which is
 * what the pronunciation drill wants (one phrase, one score).
 */
export function startListening(continuous: boolean, callbacks: ListenCallbacks): void {
  if (!isRecognitionSupported()) {
    callbacks.onError("This browser has no speech recognition — try Chrome or Safari, or just type.");
    return;
  }
  stopListening();
  consecutiveFailures = 0;
  unproductiveTurns = 0;
  session = { ...callbacks, continuous, heardSomething: false };
  beginTurn();
}

/** Creates a recogniser for one stretch of speech and starts it. */
function beginTurn(): void {
  const current = session;
  const Recognition = constructorFor();
  if (current === null || Recognition === null) return;

  const rec = new Recognition();
  recognition = rec;
  turnStartedAt = Date.now();
  rec.lang = 'en-US';
  rec.continuous = current.continuous;
  rec.interimResults = true;
  rec.maxAlternatives = 1;

  rec.onresult = (event) => {
    if (session !== current) return;
    consecutiveFailures = 0;
    unproductiveTurns = 0;
    let interim = '';
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      if (!result) continue;
      const text = (result[0]?.transcript ?? '').trim();
      if (!text) continue;
      if (result.isFinal) {
        current.heardSomething = true;
        current.onResult(text);
      } else {
        interim = interim ? `${interim} ${text}` : text;
      }
    }
    if (interim) current.onPartial(interim);
  };

  rec.onerror = (event) => {
    if (session !== current) return;
    // Silence and "no match" are the normal way an utterance ends, so they
    // never close a hands-free session.
    if (current.continuous && isRecoverable(event.error)) return;
    if (event.error === 'no-speech' && !current.continuous && current.heardSomething) return;
    current.onError(messageFor(event.error));
    endSession();
  };

  rec.onend = () => {
    if (session !== current) return;
    if (current.continuous) {
      scheduleNextTurn();
      return;
    }
    if (!current.heardSomething) current.onError("I didn't catch that — try again.");
    endSession();
  };

  try {
    rec.start();
  } catch (e) {
    // start() throws if the engine is still winding down from the last turn.
    console.warn('[speech] start failed', e);
    if (current.continuous && ++consecutiveFailures <= MAX_CONSECUTIVE_FAILURES) {
      scheduleNextTurn();
    } else {
      current.onError('The recogniser is busy — try again in a moment.');
      endSession();
    }
  }
}

function isRecoverable(error: string): boolean {
  switch (error) {
    case 'no-speech':
    case 'aborted':
      return true;
    case 'audio-capture':
    case 'network':
      return ++consecutiveFailures <= MAX_CONSECUTIVE_FAILURES;
    default:
      // 'not-allowed' and 'service-not-allowed' are permission refusals: no
      // amount of retrying will help.
      return false;
  }
}

/**
 * Tears the recogniser down and opens the next utterance a moment later.
 *
 * A hands-free session restarts on silence forever — that is the whole point of
 * the latch. But when the engine refuses outright (no microphone, permission
 * blocked, an embed that isn't allowed to record) every turn ends immediately
 * and restarting is futile: without the guard below the mic button would sit
 * there latched on, spinning a restart every 250 ms and never telling anyone
 * why. A genuinely silent turn lasts seconds, so only instant, empty turns
 * count towards giving up.
 */
function scheduleNextTurn(): void {
  const current = session;
  if (current === null) return;

  const productive = Date.now() - turnStartedAt >= MIN_PRODUCTIVE_TURN_MS;
  unproductiveTurns = productive ? 0 : unproductiveTurns + 1;
  if (unproductiveTurns > MAX_UNPRODUCTIVE_TURNS) {
    current.onError(
      isEmbedded()
        ? 'The microphone is blocked here because this app is embedded in another page. Open it in its own tab to speak.'
        : "The microphone kept stopping straight away. Check that this page is allowed to use it, then tap 🎤 again.",
    );
    endSession();
    return;
  }

  releaseRecognition();
  if (restartTimer !== null) clearTimeout(restartTimer);
  // Engines report "busy" if restarted in the same tick.
  restartTimer = setTimeout(() => {
    restartTimer = null;
    if (session !== null) beginTurn();
  }, RESTART_DELAY_MS);
}

export function stopListening(): void {
  const rec = recognition;
  endSession();
  try {
    rec?.stop();
  } catch {
    // already stopped
  }
}

function endSession(): void {
  session = null;
  if (restartTimer !== null) {
    clearTimeout(restartTimer);
    restartTimer = null;
  }
  releaseRecognition();
}

function releaseRecognition(): void {
  const rec = recognition;
  recognition = null;
  if (!rec) return;
  rec.onresult = null;
  rec.onerror = null;
  rec.onend = null;
  try {
    rec.abort();
  } catch {
    // already finished
  }
}

function messageFor(error: string): string {
  switch (error) {
    case 'not-allowed':
    case 'service-not-allowed':
      return isEmbedded()
        ? 'The microphone is blocked here because this app is embedded in another page. Open it in its own tab to speak.'
        : 'Microphone permission is needed for speaking practice. Allow it in your browser and tap 🎤 again.';
    case 'audio-capture':
      return 'No microphone was found, or it is being used by another app.';
    case 'network':
      return 'The speech engine needs a network connection in this browser.';
    case 'no-speech':
      return "I didn't hear anything. Tap the mic and try again.";
    case 'aborted':
      return 'Listening stopped.';
    default:
      return 'Speech recognition failed. Please try again.';
  }
}
